import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

interface ExportColumnsDialogProps {
  open: boolean;
  onClose: () => void;
  columns: GridColDef[];
  onExport: (selectedFields: string[], exportType: "excel" | "pdf") => void;
  isExporting?: boolean;
  columnVisibilityModel?: Record<string, boolean>; // برای همگام‌سازی با انتخاب‌های جدول
}

const ExportColumnsDialog: React.FC<ExportColumnsDialogProps> = ({
  open,
  onClose,
  columns,
  onExport,
  isExporting = false,
  columnVisibilityModel = {},
}) => {
  // فیلتر کردن ستون‌های قابل export (حذف actions و ستون‌هایی که نباید export شوند)
  const exportableColumns = React.useMemo(
    () =>
      columns.filter(
        (col) => col.field !== "actions" && col.type !== "actions"
      ),
    [columns]
  );

  // State برای نگهداری انتخاب‌ها
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // برای نگه‌داری وضعیت قبلی dialog
  const prevOpenRef = React.useRef<boolean>(false);

  // وقتی dialog باز می‌شود، ستون‌های visible را انتخاب کن
  // اگر columnVisibilityModel وجود داشت، از آن استفاده کن، در غیر این صورت همه را انتخاب کن
  useEffect(() => {
    // فقط وقتی dialog باز می‌شود (و قبلاً بسته بوده)
    if (open && !prevOpenRef.current) {
      // اگر columnVisibilityModel وجود دارد، فقط ستون‌های visible را انتخاب کن
      if (Object.keys(columnVisibilityModel).length > 0) {
        const visibleFields = exportableColumns
          .filter((col) => {
            // اگر فیلد در columnVisibilityModel وجود نداشته باشد، به صورت پیش‌فرض visible است
            return columnVisibilityModel[col.field] !== false;
          })
          .map((col) => col.field);
        setSelectedFields(visibleFields);
      } else {
        // اگر columnVisibilityModel خالی است، همه ستون‌ها را انتخاب کن
        setSelectedFields(exportableColumns.map((col) => col.field));
      }
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, columnVisibilityModel]); // اضافه کردن columnVisibilityModel به dependency

  const handleToggleField = (field: string) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const handleSelectAll = () => {
    if (selectedFields.length === exportableColumns.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(exportableColumns.map((col) => col.field));
    }
  };

  const handleExport = (exportType: "excel" | "pdf") => {
    if (selectedFields.length === 0) {
      return;
    }
    onExport(selectedFields, exportType);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">انتخاب ستون‌ها برای خروجی</Typography>
          <Button
            onClick={handleSelectAll}
            size="small"
            variant="outlined"
            sx={{ minWidth: 120 }}
          >
            {selectedFields.length === exportableColumns.length
              ? "لغو انتخاب همه"
              : "انتخاب همه"}
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          <FormGroup>
            {exportableColumns.map((column) => (
              <FormControlLabel
                key={column.field}
                control={
                  <Checkbox
                    checked={selectedFields.includes(column.field)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleField(column.field);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxIcon />}
                  />
                }
                label={
                  <Typography variant="body2">
                    {column.headerName || column.field}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Box>
        {selectedFields.length === 0 && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 2, display: "block" }}
          >
            لطفاً حداقل یک ستون را انتخاب کنید
          </Typography>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isExporting}>
          انصراف
        </Button>
        <Button
          onClick={() => handleExport("excel")}
          variant="contained"
          color="success"
          disabled={selectedFields.length === 0 || isExporting}
        >
          {isExporting ? "در حال آماده‌سازی..." : "خروجی Excel"}
        </Button>
        <Button
          onClick={() => handleExport("pdf")}
          variant="contained"
          color="error"
          disabled={selectedFields.length === 0 || isExporting}
        >
          {isExporting ? "در حال آماده‌سازی..." : "خروجی PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportColumnsDialog;
